import type { Expedition } from './types';
import CarvedAridMountains from './assets/CarvedAridMountains.webp';
import CapeBeach from './assets/CapeBeach.webp';
import ElephantAndBaby from './assets/ElephantAndBaby.webp';
import ThreeLions from './assets/ThreeLions.webp';
import Penguin from './assets/Penguin.webp';

export const DESTINATIONS = ['All Territories', 'Kruger National Park', 'KwaZulu-Natal', 'Western Cape'];
export const EXPERIENCES = [
	'All Collections',
	'Big 5 Safari',
	'Coastal Safari',
	'Mountain Escapes',
	'Ocean Expeditions',
	'Cultural Heritage',
];

export const EXPEDITIONS: Expedition[] = [
	{
		id: 'kruger-experience-big-5',
		title: 'Kruger Experience – Big 5 Safari',
		subtitle: "Iconic Big 5 tracking in South Africa's legendary wilderness reserve",
		location: 'Kruger National Park',
		country: 'South Africa',
		durationDays: 5,
		durationText: 'Recommended: 5 – 10 Days',
		image: ElephantAndBaby,
		rating: 5.0,
		reviewsCount: 0,
		difficulty: 'Relaxed',
		featured: true,
		description:
			"Immerse yourself in South Africa's world-renowned Kruger National Park. Encounter the legendary Big 5—lion, leopard, rhino, elephant, and buffalo—in their natural habitat. Experience thrilling open-vehicle dawn and twilight game drives, accompanied by expert rangers and local wildlife trackers, paired with luxury bush lodge hospitality.",
		highlights: [
			'Sunrise and twilight open-vehicle 4x4 Big 5 game drives',
			'Expert certified field rangers and wildlife trackers',
			'Traditional boma dinner beneath star-studded African skies',
			'Exclusive bush walks discovering indigenous flora and tracking secrets',
		],
		accommodations:
			'Every detail is chosen for absolute integration into surrounding ecosystems, preserving top-tier luxury. Refreshing plunge pools, premium dining decks with stunning views',
		inclusions: [
			'All open-vehicle game drives with professional ranger & tracker team',
			'Luxury lodge accommodation with gourmet dining & premium beverages',
			'Kruger National Park conservation levies & entrance fees',
			'Scenic road transfers between airport and luxury lodge',
		],
		requiredPreparations: [
			'Please be aware Kruger National Park is situated in a malaria area. Malaria precautions/prophylaxis, and Dr consultation recommended before departure.',
		],
		detailedItinerary: [
			{
				day: 1,
				title: 'Arrival & Sunset Bush Welcome',
				description:
					'Arrive at the private concession airstrip and be greeted by a lodge representative. Settle into your luxury suite before embarking on your first afternoon game drive into the golden savannah.',
				activities: ['Concession Welcome Reception', 'Twilight Safari Drive', 'Fireside Boma Welcome Dinner'],
			},
			{
				day: 2,
				title: 'Dawn Tracking & Apex Predator Encounters',
				description:
					'Wake before dawn to the scent of fresh African coffee. Set off as predators are active, following fresh lion and leopard tracks along dry riverbeds and ancient game trails.',
				activities: ['Sunrise Game Drive', 'Coffee in the Savannah', 'Afternoon Wildlife Observation'],
			},
			{
				day: 3,
				title: 'Deep Wilderness Drives & River Confluences',
				description:
					'Traverse diverse habitats from dense mopane woodland to open grasslands. Watch herds of elephants drinking and bathing at vital waterholes while listening to the sounds of Africa.',
				activities: ['Daily Game Drives', 'Waterhole Photography Stop', 'Sunset Cocktails Over the Reserve'],
			},
			{
				day: 4,
				title: 'Guided Walking Safari & Starlit Boma Feast',
				description:
					'Step out on foot with an armed ranger to interpret the subtle micro-signals of the African wilderness: animal tracks, medicinal plants, and bird alarm calls.',
				activities: [
					'Morning Guided Bush Walk',
					'Relaxation at the Private Plunge Pool',
					'Gourmet Starlit Safari Feast',
				],
			},
			{
				day: 5,
				title: 'Final Morning Game Drive & Farewell',
				description:
					'Enjoy a final early morning safari before a hearty farewell breakfast and scheduled transfer for your onward journey.',
				activities: ['Sunrise Safari Drive', 'Farewell Lodge Brunch', 'Airport Transfer'],
			},
		],
	},
	{
		id: 'beach-to-big-5-safari',
		title: 'From the Beach to the Big 5 – Safari',
		subtitle: 'Pristine Indian Ocean coastlines meet premier Big 5 private reserves',
		location: 'KwaZulu-Natal',
		country: 'South Africa',
		durationDays: 7,
		durationText: 'Recommended: 7 Days Minimum',
		image: ThreeLions,
		rating: 5.0,
		reviewsCount: 0,
		difficulty: 'Relaxed',
		featured: true,
		description:
			'Experience the finest dual journey in KwaZulu-Natal. Transition seamlessly from the warm, golden shores of the Coast and coastal dunes to premier private Big 5 game reserves. Spot magnificent marine life in crystal-clear waters before heading inland to track lions, cheetahs, and rhinos across rolling acacia savannahs.',
		highlights: [
			'Combine secluded Indian Ocean beaches with premier Big 5 game reserves',
			'Snorkeling and dolphin watching along pristine subtropical coral reefs',
			'Daily morning and evening safari drives in private wildlife concessions',
			'Sundowners overlooking sweeping coastal and savannah horizons',
		],
		accommodations:
			'Every detail is chosen for absolute integration into surrounding ecosystems, preserving top-tier luxury. Refreshing plunge pools, premium dining decks, and 24-hour assistance.',
		inclusions: [
			'All coastal and safari luxury lodge accommodations',
			'Guided marine excursions and daily open 4x4 game drives',
			'Gourmet coastal seafood and traditional safari bush dining',
			'All regional conservation fees and park entry permits',
		],
		requiredPreparations: [
			'Safari & Beach Essentials: Pack comfortable outdoor clothing, swimwear and good sun protection for both safari and coastal activities.',
		],
		detailedItinerary: [
			{
				day: 1,
				title: 'Arrival on the Subtropical Dolphin Coast',
				description:
					'Arrive on the KwaZulu-Natal coastline and travel through the lush coastal forests to your secluded beachfront lodge. Unwind to the sound of breaking Indian Ocean waves.',
				activities: ['Coastal Dune Check-in', 'Beach Stroll & Welcome Drinks', 'Fresh Seafood Dinner Under the Stars'],
			},
			{
				day: 2,
				title: 'Marine Safari & Coastal Dune Exploration',
				description:
					'Board an ocean boat for an exciting marine safari encountering resident bottlenose dolphins, seasonal whales, and vibrant coral reef snorkeling.',
				activities: ['Guided Coral Reef Snorkel', 'Ocean Dolphin Spotting', 'Coastal Sundowners'],
			},
			{
				day: 3,
				title: 'Journey Inland to Big 5 Wilderness Reserve',
				description:
					'Bid farewell to the coast and take a scenic private transfer through rolling Zululand hills directly into a pristine private Big 5 game reserve.',
				activities: [
					'Scenic Zululand Transfer',
					'Lodge Welcome & Suite Orientation',
					'Late Afternoon Predator Game Drive',
				],
			},
			{
				day: 4,
				title: 'Predator Tracking & Savannah Game Drives',
				description:
					'Spend the day tracking lions, cheetahs, and leopards across the rugged Zululand topography with your expert field guide and tracker team.',
				activities: ['Early Morning Predator Tracking', 'Bush Picnic under Wild Fig Trees', 'Twilight Game Drive'],
			},
			{
				day: 5,
				title: 'Rhino Conservation & Endangered Species Tracking',
				description:
					'Learn firsthand about vital anti-poaching initiatives and participate in dedicated tracking of both black and white rhinos roaming protected habitats.',
				activities: ['Rhino Tracking Experience', 'Conservation Insights', 'Fireside Discussion & Star Gazing'],
			},
			{
				day: 6,
				title: 'Bush Walk & Candlelit Savannah Boma',
				description:
					'Experience the thrill of walking through the bushveld on foot, followed by an afternoon relaxation session and a celebratory open-air boma banquet.',
				activities: ['Guided Morning Foot Safari', 'Lodge Deck Game Viewing', 'Traditional Safari Braai Dinner'],
			},
			{
				day: 7,
				title: 'Sunrise Safari & Onward Journey',
				description:
					'Take in the magnificent sights and sounds of the wilderness one last time during a sunrise game drive before departing for your connecting flight.',
				activities: ['Final Sunrise Safari', 'Hearty Breakfast', 'Airport Transfer'],
			},
		],
	},
	{
		id: 'big-5-mountain-drakensberg',
		title: 'Big 5 Safari and Mountain Escapes in the Drakensberg',
		subtitle: 'Majestic Drakensberg mountain amphitheaters & private Big 5 game reserves',
		location: 'KwaZulu-Natal',
		country: 'South Africa',
		durationDays: 7,
		durationText: 'Recommended: 7 Days Minimum',
		image: CarvedAridMountains,
		rating: 5.0,
		reviewsCount: 0,
		difficulty: 'Moderate',
		featured: true,
		description:
			'Unite the towering heights of the uKhahlamba-Drakensberg World Heritage Site with the raw thrill of a private Big 5 wilderness safari. Hike amidst dramatic basalt cliffs and cascading waterfalls, discover ancient San rock art, and unwind in mountain boutique luxury before entering renowned private reserves for unforgettable wildlife encounters.',
		highlights: [
			'Explore the iconic Drakensberg Amphitheatre and Tugela Gorge trails',
			'Authentic San rock art heritage walks with specialized cultural guides',
			'Big 5 safari tracking in KwaZulu-Natal’s premier private game reserves',
			'Breathtaking panoramic mountain retreats paired with fireside lodge dinners',
		],
		accommodations:
			'Every detail is chosen for absolute integration into surrounding ecosystems, preserving top-tier luxury. Refreshing plunge pools, premium dining decks, and 24-hour assistance.',
		inclusions: [
			'Curated mountain lodge and luxury safari lodge stays',
			'Guided mountain trails and daily open-vehicle game drives',
			'All meals, select wines, and regional degustation menus',
			'uKhahlamba Park permits and reserve conservation levies',
		],
		requiredPreparations: [
			'Mountain & Safari Essentials: Bring comfortable walking shoes and layered clothing, as mountain temperatures can change quickly.',
		],
		detailedItinerary: [
			{
				day: 1,
				title: 'Welcome to the Majestic Drakensberg Mountains',
				description:
					'Journey into the foothills of the Northern Drakensberg. Check into your with breathtaking views of sheer basalt cliffs and green valleys.',
				activities: ['Mountain Sanctuary Check-in', 'Valley Sunset Walk', 'Cozy Fireside Welcome Dinner'],
			},
			{
				day: 2,
				title: 'Mountain Peaks, Waterfalls & Ancient San Rock Art',
				description:
					'Embark on a guided hike through towards rushing mountain waterfalls and protected rock shelters adorned with ancient San Bushman art.',
				activities: ['Guided Mountain Hike', 'San Rock Art Interpretation', 'Afternoon Tea with Panoramic Peaks'],
			},
			{
				day: 3,
				title: 'Scenic Escarpment Drive & Transfer to Big 5 Reserve',
				description:
					'Descend from the mountain peaks through the rolling Midlands towards your private Big 5 safari concession in the heart of KwaZulu-Natal.',
				activities: ['Scenic Midlands Transit', 'Safari Lodge Arrival Reception', 'First Dusk Game Drive'],
			},
			{
				day: 4,
				title: 'Tracking Big Cats & Elephant Herds',
				description:
					'Traverse the savanna and river valleys tracking lions, leopards, rhinos, elephants, and buffalo across private wilderness concessions.',
				activities: ['Dawn Game Drive', 'Savannah Brunch', 'Twilight Wildlife Tracking with Spotlight'],
			},
			{
				day: 5,
				title: 'Full-Day Reserve Tracking & Panoramic Sundowners',
				description:
					'Explore remote corners of the reserve where cheetahs hunt on open plains and giraffe gracefully browse acacia canopy tops.',
				activities: ['Morning Big 5 Expedition', 'Waterhole Birdwatching', 'Hilltop Sunset Sundowners'],
			},
			{
				day: 6,
				title: 'Guided Bushveld Walk & Stargazing Dinner',
				description:
					'Learn the art of tracking on foot with experienced rangers, followed by an evening gourmet feast served under unpolluted African skies.',
				activities: ['Morning Walking Safari', 'Poolside Wildlife Viewing', 'Stargazing Safari Banquet'],
			},
			{
				day: 7,
				title: 'Dawn Game Drive & Mountain Departure',
				description:
					'A final thrilling early morning game drive before breakfast and transfers back to the airport for your onward travel.',
				activities: ['Dawn Game Drive & Mountain Departure', 'Farewell Lodge Breakfast', 'Airport Transfer'],
			},
		],
	},
	{
		id: 'cape-town-big-5-safari',
		title: 'Cape Town and Big 5 Safari',
		subtitle: 'Cosmopolitan Atlantic vistas, Table Mountain & premier Big 5 wildlife sanctuaries',
		location: 'Western Cape, Kruger National Park, KwaZulu-Natal',
		country: 'South Africa',
		durationDays: 9,
		durationText: 'Recommended: 9 Days Minimum',
		image: CapeBeach,
		rating: 5.0,
		reviewsCount: 0,
		difficulty: 'Relaxed',
		featured: true,
		description:
			'The ultimate fusion of urban elegance and untamed African wilderness. Begin in Cape Town surrounded by Table Mountain and the Atlantic coastline, explore the historic Cape Winelands and Cape Point penguin colonies, then fly to South Africa’s world-class game reserves for heart-pounding Big 5 tracking.',
		highlights: [
			'Private cableway ascent up Table Mountain and Cape Point coastal tour',
			'Exclusive wine tasting and gourmet cellar pairings in Franschhoek & Stellenbosch',
			'Daily Big 5 game drives in renowned private game reserves',
			'Spot the African penguin colony at Boulders Beach',
		],
		accommodations:
			'Every detail is chosen for absolute integration into surrounding ecosystems, preserving top-tier luxury. Refreshing plunge pools, premium dining decks, and 24-hour assistance. ',
		inclusions: [
			'5-star boutique hotel in Cape Town & ultra-luxury safari lodge',
			'Private guided Cape Peninsula and Winelands excursions',
			'All safari game drives with expert ranger & tracker team',
			'All national park, conservation fees, and premium dining during safari',
		],
		requiredPreparations: [
			'Variable Weather: Pack layers, comfortable footwear and sun protection for Cape Town’s changeable weather and your safari.',
		],
		detailedItinerary: [
			{
				day: 1,
				title: 'Arrival in Cape Town & V&A Waterfront Welcome',
				description:
					'Touch down in the Mother City and transfer to your luxury waterfront hotel overlooking Table Mountain and the lively harbor.',
				activities: ['Airport Meet & Greet', 'Waterfront Welcome Cocktail', 'Gourmet Harbour Dinner'],
			},
			{
				day: 2,
				title: 'Table Mountain Ascent & Historic City Highlights',
				description:
					'Ascend Table Mountain via the rotating cableway for 360-degree Atlantic vistas, followed by a private tour of the historic Bo-Kaap and Company’s Garden.',
				activities: ['Table Mountain Cable Car', 'Bo-Kaap Cultural Tour', 'Camps Bay Sunset Cocktails'],
			},
			{
				day: 3,
				title: 'Cape Peninsula, Boulders Beach Penguins & Cape Point',
				description:
					'Drive the spectacular Chapman’s Peak road, visit the famous African penguin colony at Boulders Beach, and reach the dramatic cliffs of Cape Point.',
				activities: ['Chapman’s Peak Drive', 'Boulders Beach Penguin Walk', 'Cape of Good Hope Exploration'],
			},
			{
				day: 4,
				title: 'Franschhoek & Stellenbosch Winelands Private Tasting',
				description:
					'Spend an unforgettable day touring historic wine estates, tasting award-winning vintages, and dining at world-class vineyard restaurants.',
				activities: ['Private Estate Cellar Tours', 'Sommelier Wine Pairings', 'Scenic Tour'],
			},
			{
				day: 5,
				title: 'Flight to Premier Big 5 Safari Concession',
				description:
					'Board your flight north towards South Africa’s premier private game reserves. Arrive at your luxury safari lodge in time for an afternoon game drive.',
				activities: ['Domestic Flight Transfer', 'Private reserve Arrival', 'Late Afternoon Big 5 Game Drive'],
			},
			{
				day: 6,
				title: 'Tracking Apex Predators & Twilight Safari',
				description:
					'Head out into the reserve at dawn to track lion prides and leopards before returning for a lavish bush brunch and afternoon relaxation.',
				activities: ['Sunrise Predator Drive', 'Bush Brunch Under Acacias', 'Spotlight Night Safari'],
			},
			{
				day: 7,
				title: 'Deep Wilderness Game Drives & Bush Champagne Lunch',
				description:
					'Discover the intricate biodiversity of the African savannah, encountering large elephant herds, black rhinos, and diverse bird species.',
				activities: ['Morning Wilderness Safari', 'Ethical Elephant Connection', 'Sunset Riverbank Sundowners'],
			},
			{
				day: 8,
				title: 'Night Safari & Farewell Fireplace Dinner',
				description:
					'A thrilling combination of a walking safari and twilight game drive, culminating in a celebration dinner in the open-air boma.',
				activities: ['Guided Morning Walk', 'Afternoon Wildlife Drive', 'Traditional Safari Boma Banquet'],
			},
			{
				day: 9,
				title: 'Final Morning Game Drive & Departure',
				description:
					'Savor a final dawn drive across the bushveld before enjoying a gourmet breakfast and departing for your international flight home.',
				activities: ['Final Sunrise Safari', 'Farewell Lodge Breakfast', 'Airport Transfer'],
			},
		],
	},
	{
		id: 'full-experience-cape-village-beach-safari',
		title: 'The Full Experience – Cape Town, St Lucia, Beach, Safari and Big 5',
		subtitle: 'The definitive South African journey: Cape beauty, Zulu heritage, golden beaches & Big 5',
		location: 'Western Cape and KwaZulu-Natal',
		country: 'South Africa',
		durationDays: 11,
		durationText: 'Recommended: 9 Days Minimum',
		badge: 'Best Seller',
		image: Penguin,
		rating: 5.0,
		reviewsCount: 0,
		difficulty: 'Relaxed',
		featured: true,
		description:
			'An all-encompassing grand expedition celebrating the diverse soul of South Africa. Discover iconic Cape Town landmarks, immerse yourself in UNESCO world heritage sites, enjoy scenic river cruises whilst viewing the hippos and abundant wildlife, relax along the subtropical Indian Ocean coastline, and experience exhilarating Big 5 wilderness safaris.',
		highlights: [
			'Explore Cape Town, Table Mountain, and coastal peninsulas',
			'Authentic cultural immersion in an indigenous Zulu heritage village',
			'Relaxation along the pristine, warm Indian Ocean beaches of KwaZulu-Natal',
			'Unmatched Big 5 game viewing in malaria-free private game reserves',
		],
		accommodations:
			'Every detail is chosen for absolute integration into surrounding ecosystems, preserving top-tier luxury. Refreshing plunge pools, premium dining decks, and 24-hour assistance. ',
		inclusions: [
			'Luxury accommodations across Cape Town, coastal retreats, and safari reserves',
			'Curated private cultural tours with respected village elders and guides',
			'All 4x4 open-vehicle game drives with professional naturalist trackers',
			'Full-board gourmet dining on safari and all regional conservation permits',
		],
		requiredPreparations: [
			'Pack for Variety: Bring versatile clothing, comfortable footwear, swimwear and sun protection for the different environments throughout your journey.',
		],
		detailedItinerary: [
			{
				day: 1,
				title: 'Welcome to the Mother City (Cape Town)',
				description:
					'Arrive in Cape Town and transfer to your prestigious waterfront hotel. Enjoy leisure time taking in panoramic views of Table Bay and the marina.',
				activities: ['Airport VIP Welcome', 'Luxury Suite Check-in', 'Harbourfront Dinner'],
			},
			{
				day: 2,
				title: 'Table Mountain, Atlantic Seaboard & Clifton Beaches',
				description:
					'Ascend Table Mountain via the revolving aerial cableway and tour the scenic Atlantic Seaboard through Camps Bay and Llandudno.',
				activities: ['Table Mountain Summit', 'Atlantic Coastal Drive', 'Camps Bay Sunset Dinner'],
			},
			{
				day: 3,
				title: 'Cape Point Peninsula & Boulders Beach',
				description:
					'Travel the legendary Cape Peninsula to meet the African penguins at Boulders Beach and stand at the tip of the continent at Cape Point.',
				activities: ['Chapman’s Peak Drive', 'Penguin Colony Sanctuary', 'Cape of Good Hope Trail'],
			},
			{
				day: 4,
				title: 'Flight to KwaZulu-Natal & Coastal Check-in',
				description:
					'Fly across to the subtropical coast of KwaZulu-Natal. Check into your legendary oceanfront resort overlooking the Indian Ocean.',
				activities: ['Domestic Flight Transfer', 'Oceanfront Hotel Arrival', 'High Tea on the Seafront Terrace'],
			},
			{
				day: 5,
				title: 'Authentic Zulu Heritage & Cultural Village Experience',
				description:
					'Spend a deeply inspiring day inside an authentic Zulu village, learning age-old traditions, beadwork craftsmanship, dance, and ancestral storytelling.',
				activities: [
					'Traditional Village Welcome',
					'Cultural Elder Storytelling',
					'Indigenous Crafts & Music Demonstration',
				],
			},
			{
				day: 6,
				title: 'Subtropical Coastline Relaxation & Ocean Excursions',
				description:
					'Enjoy a relaxing coastal day with options for dolphin-spotting boat trips, warm ocean swimming, or revitalizing seaside spa treatments.',
				activities: ['Morning Coastal Walk', 'Sunset Hippo Cruise', 'iSimangaliso and Cape Vidal'],
			},
			{
				day: 7,
				title: 'Transfer to Premier Big 5 Wilderness Reserve',
				description:
					'Head inland through the verdant Zululand hills to your exclusive private Big 5 game lodge, arriving for an exhilarating late-afternoon safari.',
				activities: ['Scenic In-land Drive', 'Safari Lodge Welcome', 'Sunset Big 5 Safari Drive'],
			},
			{
				day: 8,
				title: 'Tracking the Big 5: Lions, Leopards, Elephants & Rhinos',
				description:
					'Join expert local trackers to seek out Africa’s most formidable wildlife species in pristine private wilderness concessions.',
				activities: ['Dawn Tracking Drive', 'Savannah Picnic Breakfast', 'Twilight Predator Tracking'],
			},
			{
				day: 9,
				title: 'Conservation Insights & Cheetah Rehabilitation Program',
				description:
					'Engage with conservationists protecting endangered species including rhinos and cheetahs, observing successful rewilding projects.',
				activities: ['Ethical Elephant Connection', 'Rhino Orphanage Visit', 'Savannah Sundowners'],
			},
			{
				day: 10,
				title: 'Guided Bushveld Walk & Gala Bush Boma Feast',
				description:
					'Walk quietly through the savannah with master field guides before celebrating your journey with a gala open-fire boma banquet.',
				activities: ['Morning Walking Safari', 'Afternoon Wildlife Observation', 'Grand Boma Farewell Dinner'],
			},
			{
				day: 11,
				title: 'Final Sunrise Safari Drive & Journey Home',
				description:
					'A memorable final sunrise game drive followed by breakfast and private transfer to King Shaka International Airport.',
				activities: ['Dawn Wildlife Drive', 'Farewell Breakfast', 'Airport Transfer'],
			},
		],
	},
	{
		id: 'ocean-expeditions-private-beach-lodge',
		title: 'Ocean Expeditions and Private Beach Lodge',
		subtitle: 'Exclusive marine adventures, turtle nesting sanctuaries & luxury secluded beach lodge',
		location: 'KwaZulu-Natal',
		country: 'South Africa',
		durationDays: 6,
		image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1000&q=85',
		rating: 5.0,
		reviewsCount: 0,
		difficulty: 'Relaxed',
		featured: true,
		description:
			'Escape to an untouched paradise where ancient dune forests meet the turquoise waters of the Indian Ocean in the iSimangaliso Wetland Park UNESCO World Heritage Site. Enjoy secluded luxury beach lodge comfort, guided marine safaris to pristine coral reefs, seasonal sea turtle nesting walks, and ocean excursions with dolphins and manta rays.',
		highlights: [
			'Stay at a secluded eco-luxury beach lodge nestled behind coastal dunes',
			'Snorkeling and scuba diving in world-class marine protected coral reefs',
			'Seasonal nocturnal turtle tracking (Loggerhead and Leatherback turtles)',
			'Ocean boat safaris, sundowner lake cruises, and coastal forest walks',
		],
		accommodations:
			'Every detail is chosen for absolute integration into surrounding ecosystems, preserving top-tier luxury. Refreshing plunge pools, premium dining decks, and 24-hour assistance. ',
		inclusions: [
			'Luxury thatched beach lodge accommodation with ocean views',
			'All daily meals, beach picnics, and evening beach braai dining',
			'Guided marine excursions, snorkeling equipment, and forest walks',
			'Park conservation and marine sanctuary levies',
		],
		requiredPreparations: [
			'Ocean Essentials: Bring swimwear, sun protection and suitable clothing for beach and ocean activities.',
		],
		detailedItinerary: [
			{
				day: 1,
				title: 'Arrival into the Untamed Coastal Wilderness',
				description:
					'Journey into the protected coastal forest dunes of the Wetland Park to arrive at your secluded eco-luxury beach retreat.',
				activities: ['Dune Forest Check-in', 'Sunset Beach Stroll', 'Fresh Coastal Dinner'],
			},
			{
				day: 2,
				title: 'Coral Reef Snorkeling & Ocean Safari Expedition',
				description:
					'Launch directly into the warm Indian Ocean to explore protected coral reefs teeming with colorful tropical fish, manta rays, and sea turtles.',
				activities: ['Guided Coral Reef Snorkel', 'Ocean Marine Boat Safari', 'Seafood Lunch'],
			},
			{
				day: 3,
				title: 'Coastal Forest Walks & Sundowner Cruise',
				description:
					'Discover the rich birdlife and ancient botanical wonders of coastal dune forests, followed by a tranquil afternoon cruise through a protected estuary.',
				activities: ['Dune Forest Birding Walk', 'Hippo & Crocodile Spotting', 'Lake Shore Sundowners'],
			},
			{
				day: 4,
				title: 'Turtle Nesting Experience & Star Gazing',
				description:
					'Experience the magical guided walk along secluded beaches to witness Giant Leatherback and Loggerhead turtles laying eggs (Nov–Feb).',
				activities: ['Turtle Tracking Walk', 'Beach Stargazing', 'Late Night Fireside Hot Cocoa'],
			},
			{
				day: 5,
				title: 'Open Ocean Marine Tracking & Wetland Safari',
				description:
					'Enjoy free time to relax on miles of uncrowded beaches, explore the unmatched beauty of a wetland park on an open-air safari vehicle, or embark on a deep-sea scuba dive.',
				activities: ['Scuba Diving / Snorkeling Session', 'Wetland Park Safari', 'Private Beach Braai Dinner'],
			},
			{
				day: 6,
				title: 'Morning Coastal Sunrise & Departure',
				description:
					'Witness a breathtaking sunrise over the Indian Ocean before enjoying a final tropical breakfast and scheduled departure transfer.',
				activities: ['Sunrise Beach Walk', 'Farewell Lodge Breakfast', 'Departure Transfer'],
			},
		],
	},
];
